import React, { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { waLink } from "../lib/whatsapp";
import { useSEO } from "../lib/seo";
import { Mail, MessageCircle } from "lucide-react";

export default function Contact() {
  useSEO({
    title: "Contact — PrintMyTees",
    description: "Contact PrintMyTees. Email business@printmytees.in for business enquiries or support@printmytees.in for customer support. WhatsApp +91 9566853582.",
  });

  const [form, setForm] = useState({ name: "", phone: "", email: "", instructions: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const phone = form.phone.replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/quotes", { ...form, phone, product_type: "Contact Enquiry", quantity: "-", source: "contact" });
      toast.success("Thanks! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", instructions: "" });
    } catch {
      toast.error("Something went wrong. Please WhatsApp us.");
    } finally {
      setBusy(false);
    }
  };

  const focusClass = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2";

  return (
    <div className="container-pmt py-10 sm:py-12" data-testid="contact-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <div className="overline">Contact</div>
          <h1 className="display-h2 mt-2">Let's build your idea.</h1>
          <p className="mt-4 text-stone-700">
            Questions, custom orders, bulk requirements or support — we're here to help.
          </p>
          <p className="mt-2 text-sm text-stone-500">
            Choose the contact option that fits your enquiry, or send us a message below.
          </p>

          <div className="mt-7 grid gap-3 text-sm">
            <a href={waLink("Hi PrintMyTees, I have an enquiry.")} target="_blank" rel="noreferrer" className={`card-pmt p-4 flex items-start gap-3 hover:border-[#D95328] transition-colors ${focusClass}`} data-testid="contact-wa-card">
              <MessageCircle size={20} className="text-[#D95328] mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                <span className="block text-xs text-stone-500 uppercase tracking-wider">WhatsApp</span>
                <span className="block mt-1 font-semibold">+91 9566853582</span>
                <span className="block mt-1 text-xs text-stone-500">A convenient way to discuss custom orders and artwork.</span>
              </span>
            </a>

            <a href="mailto:support@printmytees.in" className={`card-pmt p-4 flex items-start gap-3 hover:border-[#D95328] transition-colors ${focusClass}`} data-testid="contact-email-card">
              <Mail size={20} className="text-[#D95328] mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                <span className="block text-xs text-stone-500 uppercase tracking-wider">Customer Support</span>
                <span className="block mt-1 font-semibold break-words">support@printmytees.in</span>
                <span className="block mt-1 text-xs text-stone-500">For customer questions and order support.</span>
              </span>
            </a>

            <a href="mailto:business@printmytees.in" className={`card-pmt p-4 flex items-start gap-3 hover:border-[#D95328] transition-colors ${focusClass}`} data-testid="contact-business-card">
              <Mail size={20} className="text-[#D95328] mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                <span className="block text-xs text-stone-500 uppercase tracking-wider">Business Enquiries</span>
                <span className="block mt-1 font-semibold break-words">business@printmytees.in</span>
                <span className="block mt-1 text-xs text-stone-500">For corporate, college, bulk and business enquiries.</span>
              </span>
            </a>
          </div>

          <div className="mt-6 border border-[#E6DFD5] bg-[#F3EDE2] p-4">
            <div className="overline">Which option should I use?</div>
            <div className="mt-3 grid gap-2 text-sm text-stone-700">
              <div><span className="font-semibold">WhatsApp:</span> custom orders, artwork and quick discussions.</div>
              <div><span className="font-semibold">Support email:</span> customer questions and order support.</div>
              <div><span className="font-semibold">Business email:</span> corporate, college, bulk and business enquiries.</div>
            </div>
          </div>

          <div className="mt-6 text-xs text-stone-500">PrintMyTees.in — Tamil Nadu, India</div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a href={waLink("Hi PrintMyTees, I have an enquiry.")} target="_blank" rel="noreferrer" className={`btn-whatsapp w-full sm:w-auto justify-center ${focusClass}`} data-testid="contact-wa-btn">
              <MessageCircle size={14} aria-hidden="true" /> WhatsApp Us
            </a>
            <a href="mailto:support@printmytees.in" className={`btn-outline-pmt w-full sm:w-auto justify-center ${focusClass}`} data-testid="contact-mail-btn">
              <Mail size={14} aria-hidden="true" /> Email Support
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="card-pmt p-4 sm:p-6" data-testid="contact-form" aria-busy={busy}>
          <div className="overline">Send a message</div>
          <h2 className="text-xl font-bold mt-1">Tell us what you need.</h2>
          <p className="mt-2 text-sm text-stone-500">
            We'll review your message and get back to you through the contact details you provide.
          </p>

          <div className="mt-5">
            <div className="overline">01 · Your details</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="label-pmt">Name <span aria-hidden="true">*</span></label>
                <input id="contact-name" required autoComplete="name" className="input-pmt" placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
              </div>
              <div>
                <label htmlFor="contact-phone" className="label-pmt">Phone <span aria-hidden="true">*</span></label>
                <input id="contact-phone" required type="tel" inputMode="numeric" autoComplete="tel" pattern="[6-9][0-9]{9}" maxLength={10} className="input-pmt" placeholder="10-digit mobile number" value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="contact-phone" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-email-input" className="label-pmt">Email <span aria-hidden="true">*</span></label>
                <input id="contact-email-input" required type="email" autoComplete="email" className="input-pmt" placeholder="you@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" />
              </div>
            </div>
          </div>

          <div className="mt-7 pt-6 border-t border-[#E6DFD5]">
            <div className="overline">02 · Your message</div>
            <div className="mt-3">
              <label htmlFor="contact-message" className="label-pmt">Message <span aria-hidden="true">*</span></label>
              <textarea id="contact-message" required rows={6} className="input-pmt" placeholder="Tell us about your question, order, artwork or requirement." value={form.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="contact-message" />
            </div>
          </div>

          <button type="submit" disabled={busy} aria-busy={busy} className={`btn-primary w-full sm:w-auto mt-6 justify-center disabled:opacity-60 ${focusClass}`} data-testid="contact-submit">
            {busy ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
